import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useCourses() {
  return useQuery({
    queryKey: ["courses_catalog"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("courses")
        .select("*")
        .eq("is_active", true)
        .order("cycle", { ascending: true })
        .order("title", { ascending: true });
      if (error) throw error;
      return data;
    },
  });
}

export function useCourseApplications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["course_applications", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("course_applications")
        .select("*, courses(*)")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const apply = useMutation({
    mutationFn: async ({ courseId, motivation }: { courseId: string; motivation?: string }) => {
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase
        .from("course_applications")
        .insert({ user_id: user.id, course_id: courseId, motivation: motivation || null, status: "applied" })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course_applications", user?.id] });
    },
  });

  const cancel = useMutation({
    mutationFn: async (applicationId: string) => {
      const { error } = await supabase
        .from("course_applications")
        .delete()
        .eq("id", applicationId)
        .eq("user_id", user?.id ?? "");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course_applications", user?.id] });
    },
  });

  return {
    applications: query.data ?? [],
    isLoading: query.isLoading,
    apply,
    cancel,
  };
}
